import styled from 'styled-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OpeningHoursList } from '../OpeningHoursList';
import { DropdownIcon } from '../../svg';
import { IAvailableStore } from '../../types';
import { colors } from '../../constants';

const Title = styled.span``;
const OpenIndicatorButton = styled.div``;
const IndicatorLightContainer = styled.div``;

interface IOpenIndicatorProps {
    open: boolean;
}

interface IDropdownIconProps {
    up: boolean;
}

const NextOpening = styled.span``;
const HoursToday = styled.span``;
const IndicatorLight = styled.div<IOpenIndicatorProps>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${({ open }) => (open ? colors.open : colors.closed)};
`;

const StyledDropdownIcon = styled(DropdownIcon)<IDropdownIconProps>`
    height: 12px;
    fill: ${colors.greyDark};
    transform: ${({ up }) => up && 'rotate(180deg)'};
`;

interface IProps {
    className?: string;
    hideOpeningHours?: boolean;
    openingHours: IAvailableStore['openingHours'];
    specialHours: IAvailableStore['specialHours'];
    onOpeningHoursClick(): void;
}

const BaseOpeningHours = ({
    className,
    openingHours,
    hideOpeningHours,
    specialHours,
    onOpeningHoursClick,
}: IProps) => {
    const [showOpeningHours, setShowOpeningHours] = useState(false);
    const now = new Date();
    const { t } = useTranslation();

    const specialHoursToday = specialHours.find((specialHour) => {
        const specialHourDate = new Date(specialHour.date).setHours(0, 0, 0, 0);
        const currentDate = new Date().setHours(0, 0, 0, 0);

        return specialHourDate === currentDate;
    });

    // Example return: 2 (Tuesday)
    const todayIndex = now.getDay();

    const hoursToday = specialHoursToday
        ? specialHoursToday.hours
        : openingHours[todayIndex].hours;

    const getStartEndDateTimes = (str: string): [Date, Date] => {
        // Sample input: 09:30 - 17:00
        const startHr = Number(str.slice(0, 2));
        const startMin = Number(str.slice(3, 5));

        const endHr = Number(str.slice(-5, -3));
        const endMin = Number(str.slice(-2));

        const dateTimeStart = new Date();
        dateTimeStart.setHours(startHr, startMin, 0, 0);

        const dateTimeEnd = new Date();
        dateTimeEnd.setHours(endHr, endMin, 0, 0);

        return [dateTimeStart, dateTimeEnd];
    };

    const [startTimeToday, endTimeToday] = getStartEndDateTimes(hoursToday);

    const isCurrentlyOpen = () => {
        if (!specialHoursToday) {
            if (hoursToday === 'Gesloten') return false;

            return now >= startTimeToday && now < endTimeToday;
        }

        if (specialHoursToday.closed) return false;

        return now >= startTimeToday && now < endTimeToday;
    };

    const isOpen = isCurrentlyOpen();

    const getDateDisplay = (date: Date) => {
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);
        const openToday =
            date.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0);
        const openTomorrow =
            date.setHours(0, 0, 0, 0) === tomorrow.setHours(0, 0, 0, 0);

        if (openToday) return t('today');
        if (openTomorrow) return t('tomorrow');

        return openingHours[date.getDay()].dayName;
    };

    const getNextOpeningTimes = () => {
        const dateToCheck = new Date();
        let numLoops = 0;

        let hoursOnDate;

        const formattedSpecialHours = specialHours.reduce(
            (acc, item) => {
                acc[item.date] = item.hours;

                return acc;
            },
            {} as Record<string, string>
        );

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const dateIso = dateToCheck.toISOString().slice(0, 10); // e.g. 2022-10-27

            const isToday = dateIso === new Date().toISOString().slice(0, 10);

            // Set default hours first
            hoursOnDate = {
                date: dateToCheck,
                hours: openingHours[dateToCheck.getDay()].hours,
            };

            // If found in special date, overwrite
            if (formattedSpecialHours[dateIso]) {
                hoursOnDate = {
                    date: dateToCheck,
                    hours: formattedSpecialHours[dateIso],
                };
            }

            // If closed on date currently checking, continue checking next day
            if (hoursOnDate.hours === 'Gesloten') {
                dateToCheck.setDate(dateToCheck.getDate() + 1);

                numLoops++;

                // Break loop in case all week closed
                if (numLoops === 7) break;

                continue;
            }

            if (isToday) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const [startTime, endTime] = getStartEndDateTimes(
                    hoursOnDate.hours
                );

                // Store already closed
                if (new Date() > endTime) {
                    dateToCheck.setDate(dateToCheck.getDate() + 1);

                    continue;
                }
            }

            break;
        }

        const dateDisplay = getDateDisplay(hoursOnDate.date);

        return {
            dateDisplay: dateDisplay,
            hours: hoursOnDate.hours,
        };
    };

    const nextOpening = getNextOpeningTimes();

    const dayOrder = [
        'Maandag',
        'Dinsdag',
        'Woensdag',
        'Donderdag',
        'Vrijdag',
        'Zaterdag',
        'Zondag',
    ];

    const reorderedOpeningHours = openingHours
        .slice()
        .sort(
            (a, b) => dayOrder.indexOf(a.dayName) - dayOrder.indexOf(b.dayName)
        );

    const handleShowOpeningHoursClick = () => {
        setShowOpeningHours(!showOpeningHours);
        onOpeningHoursClick();
    };

    return (
        <div className={className}>
            <OpenIndicatorButton
                role="button"
                onClick={handleShowOpeningHoursClick}
            >
                <IndicatorLightContainer>
                    <IndicatorLight open={isOpen} />
                </IndicatorLightContainer>
                <div className="openClosed">
                    <Title>{isOpen ? t('open') : t('closed')}</Title>
                    <NextOpening>
                        {isOpen && hoursToday}
                        {!isOpen && nextOpening.hours !== 'Gesloten' && (
                            <>
                                {t('willOpen')}{' '}
                                {nextOpening.dateDisplay.toLowerCase()}{' '}
                                {t('at')} {nextOpening.hours.slice(0, 5)}
                            </>
                        )}
                    </NextOpening>
                </div>
                {!hideOpeningHours && (
                    <StyledDropdownIcon up={showOpeningHours} />
                )}
            </OpenIndicatorButton>
            {!hideOpeningHours && (
                <OpeningHoursList
                    openingHours={reorderedOpeningHours}
                    specialHours={specialHours}
                    today={openingHours[todayIndex].dayName}
                    className={showOpeningHours ? 'expanded' : ''}
                />
            )}
        </div>
    );
};

export const OpeningHours = styled(BaseOpeningHours)`
    overflow: hidden;
    transition: max-height 0.2s ease-in-out;

    ${OpenIndicatorButton} {
        display: inline-flex;
        align-items: center;
        user-select: none;
        cursor: pointer;
    }

    ${IndicatorLightContainer} {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 1rem;
        width: 1rem;
        margin-right: 0.8rem;
    }

    ${Title} {
        font-weight: 500;
    }

    ${StyledDropdownIcon} {
        margin-left: 15px;
    }

    ${HoursToday} {
        color: ${colors.greyDark};
        margin-left: 5px;
    }

    ${NextOpening} {
        margin-left: 12px;
        color: ${colors.greyDark};
    }

    ${OpeningHoursList} {
        height: auto;
        max-height: 0;
        opacity: 0;
        overflow: hidden;
        transition:
            max-height 0.25s ease-in-out,
            opacity 0.2s linear;
        will-change: max-height, opacity;
    }

    ${OpeningHoursList}.expanded {
        max-height: none;
        opacity: 1;
        overflow: visible;
        padding-top: 1rem;
    }
`;
